import { TextBadge } from '@carvana/ds-badge';
import { TextBadgeProps } from '@carvana/ds-badge';
import { decorative } from '@carvana/ds-styles';
import cx from 'classnames';
import React, { useContext } from 'react';
import UnlockTimerContext from '~/context/vdp/UnlockTimerContext';
import { useGetIsRecentlyAdded } from '~/context/vdp/VehicleContext';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import useIsInExclusivePreview from '~/hooks/vdp/selectors/useIsInExclusivePreview';
import useIsReservableVehicle from '~/hooks/vdp/selectors/useIsReservableVehicle';
import useIsWipVehicleReservedByUser from '~/hooks/vdp/selectors/useIsWipVehicleReservedByUser';
import { OnHoldBadge } from './OnHold';

function useBadge(): {
  content: React.ReactNode;
  className?: string;
  customBorderColor?: string;
  priority: TextBadgeProps['priority'];
} | null {
  const { isInitialized, shouldShowTimer } = useContext(UnlockTimerContext);
  const { isSold, purchasePending } = useAlertStatus();
  const isInExclusivePreview = useIsInExclusivePreview();
  const isReservable = useIsReservableVehicle();
  const isWipReservedByUser = useIsWipVehicleReservedByUser();
  const isRecentlyAdded = useGetIsRecentlyAdded();

  switch (true) {
    case isSold:
      return {
        content: 'Sold',
        priority: 'muted'
      };
    case !isInitialized:
      return null;
    case (isInExclusivePreview || purchasePending) && shouldShowTimer:
      return {
        content: <OnHoldBadge />,
        className: 'pointer-events-auto',
        priority: 'warning'
      };
    case isWipReservedByUser:
      return {
        content: 'Pre-ordered',
        priority: 'informational'
      };
    case purchasePending:
      return {
        content: 'Purchase in progress',
        priority: 'critical'
      };
    case isReservable:
      return {
        content: 'Pre-order now',
        priority: 'informational'
      };
    case isRecentlyAdded:
      return {
        content: 'Recent',
        className: 'bg-background-default',
        customBorderColor: decorative['4Strong'],
        priority: 'custom'
      };
    default:
      return null;
  }
}

type OwnProps = {
  className?: string;
};

export default function Badge({ className }: OwnProps) {
  const badge = useBadge();

  if (!badge) {
    return null;
  }

  const {
    content,
    className: badgeClassName,
    customBorderColor,
    priority
  } = badge;

  return (
    <div data-testid="hero-badge">
      <TextBadge
        customBorderColor={customBorderColor}
        className={cx(className, badgeClassName)}
        priority={priority}
        variant="outline"
      >
        {content}
      </TextBadge>
    </div>
  );
}
